import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster"; // estende L com L.markerClusterGroup()
import "../sass/components/mapaClientes.scss"; 

/**
 * props.pontos: Array<{ lat: number, lng: number, nome?: string, meta?: any }>
 * Ex.: [{ lat: -23.55, lng: -46.64, nome: "Cliente A" }, ...]
 */
const MapaClientesSP = ({ pontos = [], height = 480 }) => {
  const mapRef = useRef(null);
  const clusterRef = useRef(null);

  // Bounds do Estado de SP (aprox.)
  const boundsSP = L.latLngBounds(
    L.latLng(-25.5, -53.5), // SW
    L.latLng(-19.5, -44.0)  // NE
  );

  useEffect(() => {
    if (mapRef.current) return; // init once

    const map = L.map("ssw-mapa-sp", {
      zoomControl: true,
      maxBounds: boundsSP.pad(0.2),
      maxBoundsViscosity: 0.7,
      preferCanvas: true
    });
    mapRef.current = map;

    // Tile dark + leve “tinta” azul via CSS (ver SCSS)
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      attribution:
        '&copy; <a href="https://carto.com/attributions">CARTO</a> | © OpenStreetMap'
    }).addTo(map);

    // Centraliza em SP
    map.fitBounds(boundsSP);

    // Cluster com ícone custom (bordas azuis)
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      spiderfyOnEveryZoom: false,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div class="ssw-cluster-bubble">${count}</div>`,
          className: "ssw-cluster-icon",
          iconSize: [44, 44]
        });
      }
    });
    clusterRef.current = clusterGroup;
    map.addLayer(clusterGroup);

    return () => {
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, []);

  // Renderiza/atualiza os pontos no cluster
  useEffect(() => {
    if (!mapRef.current || !clusterRef.current) return;
    const cluster = clusterRef.current;

    cluster.clearLayers();

    // DivIcon “ponto vermelho”
    const redDot = L.divIcon({
      className: "ssw-red-dot",
      iconSize: [10, 10]
    });

    const markers = [];
    for (const p of pontos) {
      if (
        !p ||
        typeof p.lat !== "number" ||
        typeof p.lng !== "number" ||
        Number.isNaN(p.lat) ||
        Number.isNaN(p.lng)
      ) continue;

      const m = L.marker([p.lat, p.lng], { icon: redDot });
      if (p.nome || p.meta) {
        const html = `
          <div class="ssw-popup">
            <strong>${p.nome ?? "Cliente"}</strong>
            ${p.meta ? `<pre>${escapeHtml(JSON.stringify(p.meta, null, 2))}</pre>` : ""}
          </div>`;
        m.bindPopup(html);
      }
      markers.push(m);
    }

    if (markers.length) {
      cluster.addLayers(markers);
      // foca nos pontos, limitando a SP
      const group = L.featureGroup(markers);
      const fit = group.getBounds();
      if (fit.isValid()) {
        const target = fit.intersects(boundsSP) ? fit : boundsSP;
        mapRef.current.fitBounds(target.pad(0.1), { maxZoom: 11 });
      }
    }
  }, [pontos]);

  return (
    <div
      id="ssw-mapa-sp"
      className="ssw-mapa-sp"
      style={{ height }}
      aria-label="Mapa de clientes ativos (SP)"
    />
  );
};

// util básico pra popup seguro
function escapeHtml(str) {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export default MapaClientesSP;
